import { type FC, useCallback, useMemo } from "react";
import { ArrowRight, Home } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

interface PropertyLoansQuestionProps extends FlowCustomComponentProps {
  propertyId: number;
}

export const PropertyLoansQuestion: FC<PropertyLoansQuestionProps> = ({
  propertyId,
  answers,
  setAnswer,
  onComplete,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, debts, deleteItem } = useEstateItems(estateId);

  const property = useMemo(() => assets.find((p) => p.id === propertyId), [assets, propertyId]);

  const mortgageKey = `has_mortgage_${propertyId}`;
  const helocKey = `has_heloc_${propertyId}`;
  const noLoansKey = `property_loans_question_${propertyId}`;

  const hasMortgage = answers[mortgageKey] === "yes";
  const hasHeloc = answers[helocKey] === "yes";
  const noLoansValue = typeof answers[noLoansKey] === "string" ? answers[noLoansKey] : "";
  const isRadioSelected = noLoansValue === "none" || noLoansValue === "not_sure";
  const hasSelection = hasMortgage || hasHeloc || isRadioSelected;

  const deleteLinkedLoans = useCallback(
    (itemType: string) => {
      debts
        .filter((d) => d.linked_item_id === propertyId && d.item_type === itemType)
        .forEach((d) => deleteItem(d.id));
    },
    [debts, propertyId, deleteItem],
  );

  const toggleCheckbox = (key: string, current: boolean) => {
    setAnswer(key, current ? "" : "yes");
    if (current) {
      const loanType = key === mortgageKey ? "Mortgages" : "Home equity loans";
      deleteLinkedLoans(loanType);
      const otherKey = key === mortgageKey ? helocKey : mortgageKey;
      if (answers[otherKey] !== "yes") {
        setAnswer(noLoansKey, "");
      }
    } else {
      setAnswer(noLoansKey, "has_loans");
    }
  };

  const selectRadio = (value: "none" | "not_sure") => {
    setAnswer(noLoansKey, value);
    if (hasMortgage) deleteLinkedLoans("Mortgages");
    if (hasHeloc) deleteLinkedLoans("Home equity loans");
    setAnswer(mortgageKey, "");
    setAnswer(helocKey, "");
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col">
      {property && (
        <div className="mb-4 flex justify-center">
          <Badge variant="outline" className="gap-1.5 border-none bg-white py-1.5 text-secondary">
            <Home className="h-3.5 w-3.5 text-purple-600" aria-hidden="true" />
            {property.item_name}
          </Badge>
        </div>
      )}

      <h2 className="text-center">What loans were on this property?</h2>
      <p className="mt-2 text-center">
        Each one is paid from the estate when the property is sold or transferred. Select all that
        apply.
      </p>

      <div className="mt-8 flex flex-col gap-3">
        <OptionCard
          label="Mortgage"
          description="The primary loan used to purchase the home"
          selected={hasMortgage && !isRadioSelected}
          onClick={() => toggleCheckbox(mortgageKey, hasMortgage)}
          variant="checkbox"
        />

        <OptionCard
          label="Home equity line of credit (HELOC)"
          description="A revolving line of credit borrowed against the home's value"
          selected={hasHeloc && !isRadioSelected}
          onClick={() => toggleCheckbox(helocKey, hasHeloc)}
          variant="checkbox"
        />

        <div className="flex items-center gap-3 py-1">
          <div className="h-px flex-1 bg-border-primary" />
          <span className="text-sm text-secondary">OR</span>
          <div className="h-px flex-1 bg-border-primary" />
        </div>

        <RadioGroup
          value={isRadioSelected ? noLoansValue : undefined}
          onValueChange={(v) => selectRadio(v as "none" | "not_sure")}
          className="flex flex-col gap-3"
        >
          <OptionCard
            label="No loans, the property was paid off"
            selected={noLoansValue === "none"}
            onClick={() => selectRadio("none")}
            variant="radio"
            value="none"
          />
          <OptionCard
            label="Not sure"
            selected={noLoansValue === "not_sure"}
            onClick={() => selectRadio("not_sure")}
            variant="radio"
            value="not_sure"
          />
        </RadioGroup>
      </div>

      <div className="mt-10 flex flex-col items-center">
        <Button
          variant="brand"
          rounded={true}
          onClick={() => onComplete()}
          disabled={!hasSelection}
          className="w-full"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
